import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';
import Computation from '@js/modules/computation';

import api from '@js/actions/api';
import {closeOpenMenus, hasOpenMenu} from '@js/components/themes/actions/clickOutside';

import MapInterface, {localFeatureName} from '@js/modules/maps/mapInterface';
import {fanColocatedMarkers} from '@js/modules/maps/markerFan';
import {isNightStop, waypointIconPath} from '@js/modules/waypointKinds';


// Dynamic loading of nearby POIs while POI/Image mode is on
const NEARBY_POI_MIN_ZOOM = 12; // below this the viewport is too wide to be relevant
const NEARBY_POI_LIMIT = 60;    // cap markers per fetch to keep the map light
const NEARBY_POI_DEBOUNCE = 350; // ms after the map stops moving before refetching

const RidePoisMapModule = {
    _poiModel: {
        isRide: false,

        // Poi data by ride ids
        poiData: {},
        poiFeatures: {},
        poiMarkersPoints: [],

        // Trigger when poi are loaded
        poiLoadedCallback: [],

        // Trigger when poi image is clicked on map
        poiImageClicked: [],

        // POI mode: clicking on the map adds a POI instead of a route point
        poiAddClicked: [],

        // Trigger when a POI marker is clicked to be edited
        poiEditClicked: [],

        // Trigger when a POI marker is clicked, to highlight it in the list
        poiHighlighted: [],

        // Trigger when a POI marker is dragged (lets the parent persist the move)
        poiMoved: [],

        // Trigger on right-click / long-touch on a POI marker (delete request)
        poiDeleteRequested: [],

        // Registered map 'click' handler while POI mode is enabled
        poiModeClickHandler: null,

        poiPictureButtonId: 'ride-poi-picture',

        // Fullscreen lightbox opened when a photo thumbnail is clicked
        poiLightbox: null,
        poiLightboxKeyHandler: null,

        // Nearby POIs loaded dynamically from the viewport while POI mode is on
        nearbyPoiEnabled: false,
        nearbyPoiMarkersById: {},
        nearbyPoiMoveHandler: null,

        // External geolocated photos (Wikimedia Commons) shown alongside them
        externalMediaMarkersById: {},

        // Preview markers for the POIs suggested along the route (not persisted)
        suggestedPoiMarkers: []
    },

    fetchRidePOIs(rideId) {
        if (rideId && rideId !== 'new') {
            this._poiModel.isRide = true;
        }

        const params = {
            map: {}
        };

        api.get(`/api/v1/rides/${rideId}/points-of-interest`, params, {
            priorityLow: true
        })
            .then((poiData) => {
                if (this._model.isDestroyed || !poiData || Utils.isEmpty(poiData.pois)) {
                    return;
                }

                this._poiModel.poiData[rideId] = poiData.pois;

                const rideIds = this._mapModel.ridesData?.map((ride) => ride.rideId);
                if (!this._mapModel.ridesData || (Utils.isPresent(rideIds) && rideIds.includes(rideId))) {
                    this._buildPois(rideId);

                    this._drawRidePois(rideId);

                    this._poiLoadedFuncs(rideId, this._poiModel.poiData[rideId]);
                }
            });

        this._addPoiPopupMarkerEvents();
    },

    addRidePoi(poiParams, rideId = 'new', options = {}) {
        if (!poiParams) {
            return;
        }

        const body = {poi: poiParams};
        // A "place" POI (shared with everyone) is created through the plan branch
        if (options.plan) {
            body.plan = true;
        }
        // On a saved ride, attach the POI to it right away so it persists on reload without
        // requiring a full ride save (an orphan POI would otherwise be lost). 'new' rides don't
        // exist server-side yet, so their POIs stay orphans until the ride is created.
        else if (rideId && rideId !== 'new') {
            body.ride_id = rideId;
        }

        return api.post('/api/v1/points-of-interest', body)
            .then((poiData) => {
                if (!poiData || Utils.isEmpty(poiData.poi)) {
                    return;
                }

                // Skip drawing when the caller manages the markers itself (draft redraw), or the map is gone
                if (options.skipDraw || this._model.isDestroyed) {
                    return poiData.poi;
                }

                this._poiModel.poiData[rideId] ||= [];
                this._poiModel.poiData[rideId].push(poiData.poi);

                this._redrawAllPois();

                if (options.centerMap) {
                    this._model.mapBounds = null;
                    this._fitMapBounds({withPois: true});
                }

                return poiData.poi;
            });
    },

    updateRidePoi(poiId, poiParams, rideId = 'new') {
        return api.update(`/api/v1/points-of-interest/${poiId}`, {poi: poiParams})
            .then((updatedPoi) => {
                if (!updatedPoi || Utils.isEmpty(updatedPoi.poi)) {
                    return;
                }

                if (this._model.isDestroyed) {
                    return updatedPoi.poi;
                }

                this._poiModel.poiData[rideId] = this._poiModel.poiData[rideId].replaceOrAdd('id', updatedPoi.poi);

                this.clearRidePoiMarkersPoints();

                this._buildPois(rideId);

                this._drawRidePois(rideId);

                return updatedPoi.poi;
            });
    },

    removeRidePoi(poiId, rideId = 'new') {
        return api.delete(`/api/v1/points-of-interest/${poiId}`)
            .then(() => {
                if (this._model.isDestroyed) {
                    return poiId;
                }

                this._poiModel.poiData[rideId] = this._poiModel.poiData[rideId].filter((poiData) => poiData.id !== poiId);

                this.clearRidePoiMarkersPoints();

                this._buildPois(rideId);

                this._drawRidePois(rideId);

                return poiId;
            });
    },

    fitRidePoisBounds(bounds) {
        if (this._poiModel.poiMarkersPoints.length > 0) {
            for (let p = 0; p < this._poiModel.poiMarkersPoints.length; p++) {
                bounds = bounds.extend(this._poiModel.poiMarkersPoints[p].marker.getLngLat());
            }
        }
    },

    // Recenter the map on a POI and open its popup — used by the ride-show waypoints timeline
    // (click a row to locate it on the map). Falls back to the stored coordinates for photo POIs
    // whose thumbnail marker is not tracked in poiMarkersPoints.
    focusPoiById(poiId, {zoom} = {}) {
        if (!this._model.map) {
            return;
        }

        const markerPoint = this._poiModel.poiMarkersPoints.find((point) => point.markerId === poiId);
        let lngLat = markerPoint && markerPoint.marker && markerPoint.marker.getLngLat();

        if (!lngLat) {
            let poi;
            Object.keys(this._poiModel.poiData || {}).some((rideId) => {
                poi = (this._poiModel.poiData[rideId] || []).find((entry) => entry.id === poiId);
                return !!poi;
            });

            if (poi && poi.latitude && poi.longitude) {
                lngLat = {lat: poi.latitude, lng: poi.longitude};
            }
        }

        if (!lngLat) {
            return;
        }

        // Frame the point at a comfortable, consistent zoom: close enough to read the surroundings
        // but never right on top of it (zoom in from an overview, pull back from a tight view).
        const FOCUS_ZOOM = 14;

        this._model.map.easeTo({
            center: lngLat,
            zoom: zoom || FOCUS_ZOOM,
            duration: 600
        });

        if (markerPoint && markerPoint.marker && typeof markerPoint.marker.togglePopup === 'function') {
            const popup = markerPoint.marker.getPopup && markerPoint.marker.getPopup();
            if (!popup || !popup.isOpen || !popup.isOpen()) {
                markerPoint.marker.togglePopup();
            }
        }
    },

    clearRidePoiMarkersPoints() {
        if (!this._model.map) {
            return;
        }

        // A marker removed under the cursor may never fire its mouseleave, which is the only thing
        // that lowers this flag — and a raised flag swallows the next map click (one lost point).
        this._model.hasPopup = false;

        for (let i = 0; i < this._poiModel.poiMarkersPoints.length; i++) {
            this._poiModel.poiMarkersPoints[i].marker.remove();
        }
        this._poiModel.poiMarkersPoints = [];
    },

    // Fully drop every POI attached to the map (markers + cached data + nearby/external media),
    // used when the whole route is cleared so no orphan POI stays on the map.
    clearAllRidePois() {
        this.clearRidePoiMarkersPoints();
        this.clearNearbyPoiMarkers();
        this.clearExternalMediaMarkers();
        this._clearWaypointGhost();
        this.clearSuggestedPoiMarkers();

        this._poiModel.poiData = {};
        this._poiModel.poiFeatures = {};
    },

    togglePoiMode(enabled) {
        this._model.poiModeEnabled = enabled;

        if (!this._model.map) {
            return;
        }

        // Hint the add-POI action with a crosshair cursor
        const canvas = this._model.map.getCanvas();
        if (canvas) {
            canvas.style.cursor = enabled ? 'crosshair' : '';
        }

        // Register the click handler lazily and keep it: the flag gates its effect
        if (enabled && !this._poiModel.poiModeClickHandler) {
            this._poiModel.poiModeClickHandler = (event) => {
                // A click that closes an open menu only closes it
                if (!this._model.poiModeEnabled || hasOpenMenu()) {
                    return;
                }

                this._poiAddClickedFuncs({
                    lat: event.lngLat.lat,
                    lng: event.lngLat.lng
                });
            };

            this._model.map.on('click', this._poiModel.poiModeClickHandler);
        }

        // Surface nearby POIs from the viewport while the mode is active
        this.toggleNearbyPois(enabled);
    },

    // Waypoint mode: clicking adds a numbered stop that is snapped strictly ONTO the drawn track.
    // A semi-transparent numbered "ghost" follows the cursor's projection on the curve.
    toggleWaypointMode(enabled) {
        this._model.waypointModeEnabled = enabled;

        if (!this._model.map) {
            return;
        }

        const canvas = this._model.map.getCanvas();
        if (canvas) {
            canvas.style.cursor = enabled ? 'crosshair' : '';
        }

        if (enabled) {
            if (!this._poiModel.waypointClickHandler) {
                this._poiModel.waypointClickHandler = (event) => {
                    if (!this._model.waypointModeEnabled || hasOpenMenu()) {
                        return;
                    }

                    const projected = this.projectOntoPolyline
                        ? this.projectOntoPolyline({lat: event.lngLat.lat, lng: event.lngLat.lng})
                        : null;

                    // Waypoints only live on the curve: ignore clicks when there is no route.
                    if (!projected) {
                        return;
                    }

                    this._poiAddClickedFuncs({
                        lat: projected.lat,
                        lng: projected.lng,
                        waypoint: true,
                        number: this._nextWaypointNumber()
                    });
                };

                this._model.map.on('click', this._poiModel.waypointClickHandler);
            }

            if (!this._poiModel.waypointMoveHandler) {
                // Throttle to one ghost update per frame: mousemove fires very frequently and the
                // projection runs over the whole polyline.
                this._poiModel.waypointMoveHandler = (event) => {
                    if (!this._model.waypointModeEnabled || this._poiModel.waypointGhostRaf) {
                        return;
                    }

                    const lngLat = {lat: event.lngLat.lat, lng: event.lngLat.lng};

                    this._poiModel.waypointGhostRaf = requestAnimationFrame(() => {
                        this._poiModel.waypointGhostRaf = null;

                        if (this._model.waypointModeEnabled) {
                            this._updateWaypointGhost(lngLat);
                        }
                    });
                };

                this._model.map.on('mousemove', this._poiModel.waypointMoveHandler);
            }
        } else {
            if (this._poiModel.waypointMoveHandler) {
                this._model.map.off('mousemove', this._poiModel.waypointMoveHandler);
                this._poiModel.waypointMoveHandler = null;
            }

            this._clearWaypointGhost();
        }
    },

    // A waypoint is a stop placed on the track. Client-created ones carry `waypoint: true`;
    // persisted ones are identified by a defined waypoint_kind (see P4).
    _isWaypointPoi(data) {
        return !!(data && (data.waypoint || (data.waypointKind && data.waypointKind !== 'kind_undefined')));
    },

    // Ghost-preview number. Uses max(existing numbers) + 1 to match the authoritative number the
    // Svelte layer assigns on save (which is also max + 1), so the preview doesn't drift after a
    // deletion.
    _nextWaypointNumber() {
        let max = 0;

        Object.keys(this._poiModel.poiData).forEach((rideId) => {
            (this._poiModel.poiData[rideId] || []).forEach((poi) => {
                if (this._isWaypointPoi(poi) && poi.number > max) {
                    max = poi.number;
                }
            });
        });

        return max + 1;
    },

    _updateWaypointGhost(lngLat) {
        const projected = this.projectOntoPolyline ? this.projectOntoPolyline(lngLat) : null;

        if (!projected) {
            this._clearWaypointGhost();
            return;
        }

        const number = this._nextWaypointNumber();

        if (!this._poiModel.waypointGhostMarker) {
            const ghost = this.buildMarker('waypoint-circle waypoint-circle-ghost', {lat: projected.lat, lng: projected.lng}, {
                anchor: 'center',
                opacity: '0.5',
                opacityWhenCovered: '0.5',
                markerContent: `<span class="waypoint-circle-number">${this._escapeHtml(String(number))}</span>`
            });

            if (!ghost) {
                return;
            }

            ghost.addTo(this._model.map);
            this._poiModel.waypointGhostMarker = ghost;
        } else {
            const numberSpan = this._poiModel.waypointGhostMarker.getElement().querySelector('.waypoint-circle-number');
            if (numberSpan) {
                numberSpan.textContent = String(number);
            }

            this._poiModel.waypointGhostMarker.setLngLat({lat: projected.lat, lng: projected.lng});
        }
    },

    _clearWaypointGhost() {
        if (this._poiModel.waypointGhostRaf) {
            cancelAnimationFrame(this._poiModel.waypointGhostRaf);
            this._poiModel.waypointGhostRaf = null;
        }

        if (this._poiModel.waypointGhostMarker) {
            this._poiModel.waypointGhostMarker.remove();
            this._poiModel.waypointGhostMarker = null;
        }
    },

    // Map an OpenMapTiles-style POI feature (class/subclass) to one of our waypoint kinds.
    _waypointKindFromFeature(props) {
        // A subclass of '' or 'yes' is the tag's placeholder: the kind then sits in class (water_point/yes).
        const subclass = String(props.subclass || '').toLowerCase();
        const value = subclass && subclass !== 'yes' ? subclass : String(props.class || '').toLowerCase();

        // Parking is not suggested on-route (an access concern), like the mobile's waypointKindFromFeature.
        const MAP = {
            viewpoint: 'viewpoint',
            drinking_water: 'water',
            water_point: 'water',
            watering_place: 'water',
            spring: 'water',
            alpine_hut: 'refuge',
            wilderness_hut: 'shelter',
            shelter: 'shelter',
            camp_site: 'camp',
            caravan_site: 'camp',
            peak: 'summit',
            saddle: 'pass',
            mountain_pass: 'pass'
        };

        return MAP[value] || null;
    },

    // Suggest POIs (water, huts, viewpoints, summits…) on/near the drawn route, read from the
    // pmaps vector tiles currently loaded. NOTE: POIs only exist in high-zoom tiles (z14+), so this
    // only covers the part of the route within the current view at a POI-bearing zoom — full-route
    // coverage would need a server-side POI-along-route query. Returns [{latitude, longitude,
    // waypointKind, name, distanceAlong}].
    suggestPoisAlongRoute(maxDistanceMeters = 150, limit = 30) {
        if (!this._model.map || !this.projectOntoPolyline) {
            return [];
        }

        const bounds = this._routeBounds();
        if (!bounds) {
            return [];
        }

        const style = this._model.map.getStyle();
        if (!style || !style.layers) {
            return [];
        }

        // Every symbol layer from a POI-ish source-layer (poi, mountain_peak, … in the OpenMapTiles
        // schema): summits/passes live in `mountain_peak`, not `poi`, so a single layer isn't enough.
        const querySpecs = new Set();
        style.layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer['source-layer'] &&
                /poi|mountain_peak/i.test(layer['source-layer']) && this._model.map.getLayer(layer.id)) {
                querySpecs.add(`${layer.source} ${layer['source-layer']}`);
            }
        });

        let features = [];
        querySpecs.forEach((spec) => {
            const [source, sourceLayer] = spec.split(' ');
            try {
                features = features.concat(this._model.map.querySourceFeatures(source, {sourceLayer}) || []);
            } catch {
                // Ignore a source that isn't queryable and keep the others.
            }
        });

        if (!features.length) {
            return [];
        }

        const margin = maxDistanceMeters / 111_000; // ~m → deg

        const seen = new Set();
        const suggestions = [];

        features.forEach((feature) => {
            const geometry = feature.geometry;
            // The tiles pack nearby POIs of one kind into a single MultiPoint: each point is a candidate.
            const points = geometry?.type === 'Point' ? [geometry.coordinates]
                : geometry?.type === 'MultiPoint' ? geometry.coordinates : [];
            if (!points.length) {
                return;
            }

            const kind = this._waypointKindFromFeature(feature.properties || {});
            if (!kind) {
                return;
            }

            points.forEach(([lng, lat]) => {
                // Cheap bounding-box reject before the exact distance-to-polyline.
                if (lat < bounds.minLat - margin || lat > bounds.maxLat + margin ||
                    lng < bounds.minLng - margin || lng > bounds.maxLng + margin) {
                    return;
                }

                const projected = this.projectOntoPolyline({lat, lng});
                if (!projected || projected.distanceMeters > maxDistanceMeters) {
                    return;
                }

                // Dedupe features split across tile boundaries.
                const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
                if (seen.has(key)) {
                    return;
                }
                seen.add(key);

                const props = feature.properties || {};
                suggestions.push({
                    latitude: lat,
                    longitude: lng,
                    waypointKind: kind,
                    name: localFeatureName(props) || '',
                    distanceAlong: projected.distanceAlong || 0
                });
            });
        });

        return suggestions.sort((a, b) => a.distanceAlong - b.distanceAlong).slice(0, limit);
    },

    _routeBounds() {
        const points = this._mapModel.linePoints;
        if (!points || points.length < 2) {
            return null;
        }

        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        points.forEach((point) => {
            minLat = Math.min(minLat, point.latLng.lat);
            maxLat = Math.max(maxLat, point.latLng.lat);
            minLng = Math.min(minLng, point.latLng.lng);
            maxLng = Math.max(maxLng, point.latLng.lng);
        });

        return {minLat, maxLat, minLng, maxLng};
    },

    // Split the route into daily stages: return a coordinate every `stepMeters` along the track
    // (excluding the very end). Used by the multi-day planner to place overnight stops.
    computeStageSplits(stepMeters) {
        const points = this._mapModel.linePoints;
        if (!points || points.length < 2 || !(stepMeters > 0)) {
            return [];
        }

        let total = 0;
        for (let i = 1; i < points.length; i++) {
            total += points[i].deltaDistance || this.distHaversine(points[i - 1].latLng, points[i].latLng, MapInterface.COORDINATES_PRECISION);
        }

        const splits = [];
        let cumulative = 0;
        let nextThreshold = stepMeters;

        for (let i = 1; i < points.length && splits.length < 30; i++) {
            const segStart = points[i - 1].latLng;
            const segEnd = points[i].latLng;
            const segLength = points[i].deltaDistance || this.distHaversine(segStart, segEnd, MapInterface.COORDINATES_PRECISION);
            const prevCumulative = cumulative;
            cumulative += segLength;

            // Interpolate the exact threshold point on this segment (avoids stacking several stops
            // on the same vertex when one segment is longer than stepMeters). Don't place a stop in
            // the last 10% of the route (that's the finish).
            while (cumulative >= nextThreshold && nextThreshold < total * 0.9 && splits.length < 30) {
                const frac = segLength > 0 ? (nextThreshold - prevCumulative) / segLength : 0;
                splits.push({
                    lat: segStart.lat + frac * (segEnd.lat - segStart.lat),
                    lng: segStart.lng + frac * (segEnd.lng - segStart.lng),
                    distance: nextThreshold
                });
                nextThreshold += stepMeters;
            }
        }

        return splits;
    },

    // Drop an overnight stop at a computed stage split (reuses the waypoint-add path).
    addStageWaypoint(split) {
        if (!split) {
            return;
        }

        this._poiAddClickedFuncs({
            lat: split.lat,
            lng: split.lng,
            waypoint: true,
            waypointKind: 'bivouac',
            overnight: true,
            skipLimit: true,
            number: this._nextWaypointNumber()
        });
    },

    // Add a suggested POI as a numbered waypoint, reusing the normal waypoint-add path.
    addSuggestedWaypoint(suggestion) {
        if (!suggestion) {
            return;
        }

        this._poiAddClickedFuncs({
            lat: suggestion.latitude,
            lng: suggestion.longitude,
            waypoint: true,
            waypointKind: suggestion.waypointKind,
            name: suggestion.name,
            skipLimit: true,
            number: this._nextWaypointNumber()
        });
    },

    // Preview the suggested POIs ON the map: a list of names never said WHERE they were, which is
    // the only thing that makes a suggestion decidable ("water point at 12.4 km" can be 300 m down
    // the valley). These markers are not persisted — one becomes a real numbered waypoint only when
    // "Add" is pressed in its popup.
    showSuggestedPoiMarkers(suggestions, onPick) {
        this.clearSuggestedPoiMarkers();

        if (!this._model.map || !Array.isArray(suggestions)) {
            return;
        }

        suggestions.forEach((suggestion) => {
            const marker = this.buildMarker('poi-suggestion-marker', {
                lat: suggestion.latitude,
                lng: suggestion.longitude
            }, {anchor: 'center'});

            if (!marker) {
                return;
            }

            // The panel list used to be the only keyboard path to a suggestion; now that the map is
            // the interface, each marker carries the role, the label and the keys itself.
            const element = marker.getElement();
            element.setAttribute('role', 'button');
            element.setAttribute('tabindex', '0');
            element.setAttribute('aria-label', this._suggestedPoiLabel(suggestion));
            element.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    marker.togglePopup();
                }
            });

            element.addEventListener('click', (event) => {
                if (hasOpenMenu()) {
                    event.stopPropagation();
                    closeOpenMenus();
                    return;
                }

                event.stopPropagation();
                marker.togglePopup();
            });

            marker.setPopup(
                this.buildPopup().setDOMContent(this._buildSuggestedPoiPopup(suggestion, marker, onPick))
            );
            marker.addTo(this._model.map);

            this._poiModel.suggestedPoiMarkers.push(marker);
        });
    },

    clearSuggestedPoiMarkers() {
        this._poiModel.suggestedPoiMarkers.forEach((marker) => marker.remove());
        this._poiModel.suggestedPoiMarkers = [];
    },

    _suggestedPoiLabel(suggestion) {
        const kind = this._waypointKindLabel(suggestion) || I18n.t('js.ride.form.pois.waypoint_kind.step');

        return I18n.t('js.maps.poi_suggestions.marker_label', {kind, name: suggestion.name || ''}).trim();
    },

    // Built as DOM (not an HTML string) so the Add button keeps its own listener: no delegation by
    // id on the map container, and nothing to escape.
    _buildSuggestedPoiPopup(suggestion, marker, onPick) {
        const container = document.createElement('div');
        container.className = 'poi-suggestion-popup';

        const kindLabel = this._waypointKindLabel(suggestion);
        if (kindLabel) {
            const kind = document.createElement('span');
            kind.className = 'poi-suggestion-kind';
            kind.textContent = kindLabel;
            container.appendChild(kind);
        }

        if (suggestion.name) {
            const name = document.createElement('span');
            name.className = 'poi-suggestion-name';
            name.textContent = suggestion.name;
            container.appendChild(name);
        }

        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'poi-suggestion-add';
        addButton.textContent = I18n.t('js.maps.poi_suggestions.add');
        addButton.addEventListener('click', () => this._pickSuggestedPoi(suggestion, marker, onPick));
        container.appendChild(addButton);

        return container;
    },

    _pickSuggestedPoi(suggestion, marker, onPick) {
        this.addSuggestedWaypoint(suggestion);

        marker.remove();
        this._poiModel.suggestedPoiMarkers = this._poiModel.suggestedPoiMarkers
            .filter((entry) => entry !== marker);

        if (onPick) {
            onPick(suggestion);
        }
    },

    toggleNearbyPois(enabled) {
        this._poiModel.nearbyPoiEnabled = enabled;

        if (!this._model.map) {
            return;
        }

        if (enabled) {
            if (!this._poiModel.nearbyPoiMoveHandler) {
                this._poiModel.nearbyPoiMoveHandler = Utils.debounce(() => this._refreshMapPois(), NEARBY_POI_DEBOUNCE);
                this._model.map.on('moveend', this._poiModel.nearbyPoiMoveHandler);
            }

            this._refreshMapPois();
        } else {
            if (this._poiModel.nearbyPoiMoveHandler) {
                this._model.map.off('moveend', this._poiModel.nearbyPoiMoveHandler);
                this._poiModel.nearbyPoiMoveHandler = null;
            }

            this.clearNearbyPoiMarkers();
            this.clearExternalMediaMarkers();
        }
    },

    _refreshMapPois() {
        this._refreshNearbyPois();
        this._refreshExternalMedia();
    },

    clearNearbyPoiMarkers() {
        Object.keys(this._poiModel.nearbyPoiMarkersById)
            .forEach((poiId) => {
                this._poiModel.nearbyPoiMarkersById[poiId].remove();
            });

        this._poiModel.nearbyPoiMarkersById = {};
    },

    clearExternalMediaMarkers() {
        Object.keys(this._poiModel.externalMediaMarkersById)
            .forEach((mediaId) => {
                this._poiModel.externalMediaMarkersById[mediaId].remove();
            });

        this._poiModel.externalMediaMarkersById = {};
    },

    _refreshNearbyPois() {
        if (!this._model.map || !this._poiModel.nearbyPoiEnabled) {
            return;
        }

        // Only load at a sufficient zoom, otherwise the viewport is too broad
        if (this._model.map.getZoom() < NEARBY_POI_MIN_ZOOM) {
            this.clearNearbyPoiMarkers();
            return;
        }

        const bounds = this._model.map.getBounds();
        if (!bounds) {
            return;
        }

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        this._fetchNearbyPois([sw.lat, sw.lng, ne.lat, ne.lng]);
    },

    _fetchNearbyPois(mapBounds) {
        api.get('/api/v1/points-of-interest', {
            map: true,
            limit: NEARBY_POI_LIMIT,
            filter: {
                map_bounds: mapBounds,
                with_pictures: true
            }
        }, {priorityLow: true})
            .then((poiData) => {
                // The mode may have been turned off, or the map destroyed, while the request was in flight
                if (this._model.isDestroyed || !this._poiModel.nearbyPoiEnabled) {
                    return;
                }

                this._syncNearbyPoiMarkers((poiData && poiData.pois) || []);
            });
    },

    _syncNearbyPoiMarkers(pois) {
        this._syncViewportMarkers(pois, this._poiModel.nearbyPoiMarkersById, (poi) => this._drawNearbyPoiThumb(poi));
    },

    _syncViewportMarkers(items, store, drawFn) {
        const bounds = this._model.map && this._model.map.getBounds();
        if (!bounds) {
            return;
        }

        Object.keys(store)
            .forEach((id) => {
                if (!bounds.contains(store[id].getLngLat())) {
                    store[id].remove();
                    delete store[id];
                }
            });

        items.forEach((item) => {
            if (!item || store[item.id] || !this._firstPoiPicture(item)) {
                return;
            }

            const marker = drawFn(item);
            if (marker) {
                store[item.id] = marker;
            }
        });
    },

    _drawNearbyPoiThumb(poi) {
        const coordinates = {
            lat: poi.latitude,
            lng: poi.longitude
        };

        const marker = this.buildMarker('poi-thumb-marker', coordinates, {
            draggable: false,
            anchor: 'bottom',
            markerContent: this._buildPoiThumbContent(poi)
        });

        if (!marker) {
            return null;
        }

        marker.getElement()
            .addEventListener('click', (event) => {
                event.stopPropagation();

                closeOpenMenus();
            });

        marker.addTo(this._model.map);

        return marker;
    },

    _refreshExternalMedia() {
        if (!this._model.map || !this._poiModel.nearbyPoiEnabled) {
            return;
        }

        if (this._model.map.getZoom() < NEARBY_POI_MIN_ZOOM) {
            this.clearExternalMediaMarkers();
            return;
        }

        const bounds = this._model.map.getBounds();
        if (!bounds) {
            return;
        }

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        this._fetchExternalMedia([sw.lat, sw.lng, ne.lat, ne.lng]);
    },

    _fetchExternalMedia(mapBounds) {
        api.get('/api/v1/map-media', {
            limit: NEARBY_POI_LIMIT,
            route_type: this._model.routingRouteType,
            filter: {
                map_bounds: mapBounds
            }
        }, {priorityLow: true})
            .then((mediaData) => {
                if (this._model.isDestroyed || !this._poiModel.nearbyPoiEnabled) {
                    return;
                }

                this._syncExternalMediaMarkers((mediaData && mediaData.media) || []);
            });
    },

    _syncExternalMediaMarkers(mediaItems) {
        this._syncViewportMarkers(mediaItems, this._poiModel.externalMediaMarkersById, (media) => this._drawExternalMediaThumb(media));
    },

    _drawExternalMediaThumb(media) {
        const coordinates = {
            lat: media.latitude,
            lng: media.longitude
        };

        const marker = this.buildMarker('poi-thumb-marker', coordinates, {
            draggable: false,
            anchor: 'bottom',
            markerContent: this._buildPoiThumbContent(media)
        });

        if (!marker) {
            return null;
        }

        marker.getElement()
            .addEventListener('click', (event) => {
                if (hasOpenMenu()) {
                    event.stopPropagation();
                    closeOpenMenus();
                    return;
                }

                event.stopPropagation();

                this._openPoiLightbox(media);
            });

        marker.addTo(this._model.map);

        return marker;
    },

    onPoiAddClicked(callback) {
        this._poiModel.poiAddClicked.push(callback);
    },

    onPoiEditClicked(callback) {
        this._poiModel.poiEditClicked.push(callback);
    },

    removePoiMarkerById(poiId) {
        // Drop it from the cached data, then cleanly redraw the remaining markers
        Object.keys(this._poiModel.poiData)
            .forEach((rideId) => {
                if (Array.isArray(this._poiModel.poiData[rideId])) {
                    this._poiModel.poiData[rideId] = this._poiModel.poiData[rideId].filter((poi) => poi.id !== poiId);
                }
            });

        this._redrawAllPois();
    },

    // Replace a ride's cached POIs with a fresh server list and redraw. Used after a delete, where the
    // server re-sequences the remaining numbers, so the map badges stay contiguous without a reload.
    setRidePoisData(rideId, pois) {
        if (!Array.isArray(pois)) {
            return;
        }

        this._poiModel.poiData[rideId] = pois;

        this._redrawAllPois();
    },

    onPoiHighlighted(callback) {
        this._poiModel.poiHighlighted.push(callback);
    },

    onPoiMoved(callback) {
        this._poiModel.poiMoved.push(callback);
    },

    onPoiDeleteRequested(callback) {
        this._poiModel.poiDeleteRequested.push(callback);
    },

    drawLocalPoi(poi) {
        // Draw/refresh a draft POI marker (no API call); markers are fully redrawn to avoid duplicates
        const rideId = 'new';

        this._poiModel.poiData[rideId] ||= [];

        const existingIndex = this._poiModel.poiData[rideId].findIndex((existing) => existing.id === poi.id);
        if (existingIndex > -1) {
            this._poiModel.poiData[rideId][existingIndex] = poi;
        } else {
            this._poiModel.poiData[rideId].push(poi);
        }

        this._redrawAllPois();
    },

    _redrawAllPois() {
        // Remove every HTML marker, then rebuild from the cached data (no duplicates)
        this.clearRidePoiMarkersPoints();

        Object.keys(this._poiModel.poiData)
            .forEach((rideId) => {
                if (Array.isArray(this._poiModel.poiData[rideId]) && this._poiModel.poiData[rideId].length > 0) {
                    this._buildPois(rideId);
                    this._drawRidePois(rideId);
                }
            });
    },

    /* Private Methods
    ******************** */
    _buildPois(rideId) {
        if (!this._poiModel.poiFeatures[rideId]) {
            this._poiModel.poiFeatures[rideId] = {};
        }

        this._poiModel.poiFeatures[rideId].markerInfos = [];

        for (let poiMarkerIndex = 0; poiMarkerIndex < this._poiModel.poiData[rideId].length; poiMarkerIndex++) {
            if (this._poiModel.poiData[rideId][poiMarkerIndex].type === 'PoiPoint') {
                const poiCoordinates = {
                    lat: this._poiModel.poiData[rideId][poiMarkerIndex].latitude,
                    lng: this._poiModel.poiData[rideId][poiMarkerIndex].longitude
                };

                const poiMarkerInfo = new MapInterface.MarkerInfo(
                    poiCoordinates,
                    'poi',
                    this._poiModel.poiData[rideId][poiMarkerIndex]
                );

                this._poiModel.poiFeatures[rideId].markerInfos.push(poiMarkerInfo);
            }
        }
    },

    _drawRidePois(rideId) {
        if (!rideId || !this._poiModel.poiFeatures[rideId]) {
            return;
        }

        const poiMarkerInfos = this._poiModel.poiFeatures[rideId].markerInfos;
        let poiMarkerInfo;

        // Clear old POI markers
        this._removePoiMarkersPoints(rideId);

        const newestImageNumber = poiMarkerInfos.reduce((max, info) => {
            return this._firstPoiPicture(info.data) && info.data.number > max ? info.data.number : max;
        }, 0);

        // Read-only ride: co-located photos lean apart like the mobile app (not on editable maps, where they are dragged).
        const fanSlots = new Map();
        if (this._isReadOnlyRide()) {
            const photoInfos = poiMarkerInfos.filter((info) => this._shouldUsePoiThumb(info.data));
            fanColocatedMarkers(photoInfos.map((info) => info.latLng))
                .forEach((slot, index) => fanSlots.set(photoInfos[index], slot));
        }

        // Draw POI markers
        if (poiMarkerInfos.length > 0) {
            for (let poiMarkerIndex = 0; poiMarkerIndex < poiMarkerInfos.length; poiMarkerIndex++) {
                poiMarkerInfo = poiMarkerInfos[poiMarkerIndex];

                const poiCoordinates = {
                    lat: poiMarkerInfo.latLng.lat,
                    lng: poiMarkerInfo.latLng.lng
                };

                // Photo POIs render as a circular thumbnail that morphs into a card on hover (in
                // place of the classic pin), on the planner (editable) as well as read-only rides.
                if (this._shouldUsePoiThumb(poiMarkerInfo.data)) {
                    const editable = this._model.poiEditable || !this._poiModel.isRide;

                    this._drawPoiThumbMarker(poiMarkerInfo, poiCoordinates, {
                        editable,
                        // On a read-only ride show every photo bubble is the same size; the "newest
                        // stands out" shrink only helps while editing (a freshly added photo pops).
                        prominent: !editable || (poiMarkerInfo.data && poiMarkerInfo.data.number === newestImageNumber),
                        poiMarkerIndex,
                        rideId,
                        fanSlot: fanSlots.get(poiMarkerInfo)
                    });
                    continue;
                }

                // Waypoints (stops on the track) render as a numbered circle over the curve; photo POIs
                // keep the classic pin.
                const isWaypoint = this._isWaypointPoi(poiMarkerInfo.data);

                // Popup tip sits just above the marker: the circle is centre-anchored (~26px), the pin
                // is top-anchored (~30px tall), so each needs its own offset.
                const poiPopup = this.buildPopup({
                    offset: isWaypoint ? [0, -18] : [0, -30]
                })
                    .setHTML(this._buildPoiMarkerPopup(rideId, poiMarkerIndex, poiMarkerInfo));

                // Draggable on editable maps (planner/edit), static on read-only ride views
                const isEditable = this._model.poiEditable || !this._poiModel.isRide;

                const newPoiMarkerPoint = new MapInterface.MarkerPoint(
                    this.buildMarker(
                        isWaypoint
                            ? (this._isReadOnlyRide() && isNightStop(poiMarkerInfo.data) ? 'waypoint-circle waypoint-circle--night' : 'waypoint-circle')
                            : 'poi-marker',
                        poiCoordinates,
                        {
                            draggable: isEditable,
                            markerOpacity: this._poiModel.isRide && !this._model.poiEditable ? '0.6' : undefined,
                            anchor: isWaypoint ? 'center' : 'top',
                            markerContent: isWaypoint
                                ? this._buildWaypointCircleContent(poiMarkerInfo.data)
                                : this._buildPoiPinBadge(poiMarkerInfo.data)
                        }),
                    poiMarkerInfo.data && poiMarkerInfo.data.id,
                    poiPopup
                );

                newPoiMarkerPoint.marker
                    .setPopup(newPoiMarkerPoint.popup);

                // add to map
                newPoiMarkerPoint.marker.addTo(this._model.map);

                // add to array for reference
                this._poiModel.poiMarkersPoints.push(newPoiMarkerPoint);

                // set up event handlers
                this._addPoiMarkersPointEventHandlers(newPoiMarkerPoint, poiMarkerInfo, poiMarkerIndex, rideId);
            }
        }
    },

    // Photo POIs render as a thumbnail on ride-related maps (read-only ride show + editable planner),
    // never on generic maps (explore) where the classic pin is kept.
    _shouldUsePoiThumb(poiMarkerData) {
        return (this._poiModel.isRide || this._model.poiEditable)
            && !!this._firstPoiPicture(poiMarkerData);
    },

    _firstPoiPicture(poiMarkerData) {
        if (!poiMarkerData || !Array.isArray(poiMarkerData.pictures)) {
            return null;
        }

        return poiMarkerData.pictures.find((picture) => picture && picture.mediumUrls && picture.mediumUrls.jpg) || null;
    },

    _isReadOnlyRide() {
        return this._poiModel.isRide && !this._model.poiEditable;
    },

    _drawPoiThumbMarker(poiMarkerInfo, poiCoordinates, {editable = false, prominent = true, poiMarkerIndex = 0, rideId, fanSlot} = {}) {
        const poiMarkerData = poiMarkerInfo.data;
        const fanned = !!fanSlot && fanSlot.count > 1;

        // Non-newest photos shrink so a freshly added one stands out.
        const markerClass = prominent ? 'poi-thumb-marker' : 'poi-thumb-marker poi-thumb-marker--muted';

        // Anchor at the bottom so the tip points at the exact POI location and the
        // card grows upward on hover (keeping the tip pinned to the coordinate)
        // Fanned: the bubble and its stem lean together around the stem's foot, which stays on the coordinate.
        const markerContent = fanned
            ? `<span class="poi-thumb-lean" style="--fan-angle: ${fanSlot.angleDeg}deg">${this._buildPoiThumbContent(poiMarkerData)}<span class="poi-thumb-stem" style="height: ${fanSlot.stemPx}px"></span></span>`
            : this._buildPoiThumbContent(poiMarkerData);

        const thumbMarker = this.buildMarker(markerClass, poiCoordinates, {
            draggable: editable,
            anchor: 'bottom',
            markerContent
        });

        if (!thumbMarker) {
            return;
        }

        if (fanned) {
            thumbMarker.getElement().style.setProperty('--fan-z', String(fanSlot.zIndex));
        }

        const thumbMarkerPoint = new MapInterface.MarkerPoint(thumbMarker, poiMarkerData && poiMarkerData.id, null);
        thumbMarkerPoint.marker.addTo(this._model.map);
        this._poiModel.poiMarkersPoints.push(thumbMarkerPoint);

        if (editable) {
            this._addPoiMarkersPointEventHandlers(thumbMarkerPoint, poiMarkerInfo, poiMarkerIndex, rideId);
        } else {
            thumbMarker.getElement()
                .addEventListener('click', (event) => {
                    if (hasOpenMenu()) {
                        event.stopPropagation();
                        closeOpenMenus();
                        return;
                    }

                    event.stopPropagation();

                    this._openPoiLightbox(poiMarkerData);
                });
        }
    },

    _buildPoiThumbContent(poiMarkerData) {
        const picture = this._firstPoiPicture(poiMarkerData);

        // Single line of text (title, falling back to description) with CSS ellipsis
        const captionText = poiMarkerData.title || poiMarkerData.description || '';
        const caption = captionText
            ? `<span class="poi-thumb-caption">${this._escapeHtml(captionText)}</span>`
            : '';

        // Click-to-fullscreen is wired directly on the marker element (see _drawPoiThumbMarker)
        return `
            <span class="poi-thumb-card">
                <img class="poi-thumb-img"
                     src="${this._escapeHtml(picture.mediumUrls.jpg)}"
                     alt="${this._escapeHtml(poiMarkerData.title || '')}">
                ${caption}
            </span>
            <span class="poi-thumb-tip"></span>
        `;
    },

    _escapeHtml(value) {
        const container = document.createElement('div');
        container.textContent = (value === null || value === undefined) ? '' : value;
        // textContent escapes < > &; also escape quotes since the result is used in attributes.
        return container.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },

    // Number badge + optional overnight (bivouac) marker overlaid on the classic POI pin, so a
    // waypoint reads as a numbered step. Returns undefined when there is nothing to overlay.
    // Translated place type of a waypoint (e.g. bivouac, refuge, viewpoint); null for a generic stop.
    _waypointKindLabel(poiMarkerData) {
        const kind = poiMarkerData && poiMarkerData.waypointKind;

        if (!kind || kind === 'kind_undefined' || kind === 'step') {
            return null;
        }

        return I18n.t(`js.ride.form.pois.waypoint_kind.${kind}`);
    },

    // A waypoint (stop on the track) renders as a circle sitting on the curve, not a pin: its kind icon
    // on a read-only ride (mobile parity), its planning number on editable maps.
    _buildWaypointCircleContent(poiMarkerData) {
        let content = '';

        if (isNightStop(poiMarkerData)) {
            content += `<span class="poi-marker-overnight" title="${this._escapeHtml(I18n.t('js.maps.poi.overnight'))}"></span>`;
        }

        // Read-only: the night circle already says it with its tent, no extra overnight badge.
        if (this._isReadOnlyRide()) {
            return `<svg class="waypoint-circle-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${waypointIconPath(poiMarkerData)}"/></svg>`;
        }

        const number = poiMarkerData && poiMarkerData.number ? String(poiMarkerData.number) : '';

        return content + `<span class="waypoint-circle-number">${this._escapeHtml(number)}</span>`;
    },

    // Image POIs don't show a number badge (their numbering is internal only); waypoints render
    // their number via _buildWaypointCircleContent instead.
    _buildPoiPinBadge(poiMarkerData) {
        if (!isNightStop(poiMarkerData)) {
            return undefined;
        }

        return `<span class="poi-marker-overnight" title="${this._escapeHtml(I18n.t('js.maps.poi.overnight'))}"></span>`;
    },

    // Pause length / overnight summary line shown in the POI popup (planning metadata).
    _buildPoiMeta(poiMarkerData) {
        const parts = [];

        if (poiMarkerData.pauseDuration) {
            parts.push(this._escapeHtml(`${I18n.t('js.ride.show.waypoints.pause')} ${Computation.formatDuration(Number(poiMarkerData.pauseDuration), {withSeconds: false})}`));
        }

        if (isNightStop(poiMarkerData)) {
            parts.push(this._escapeHtml(I18n.t('js.maps.poi.overnight')));
        }

        return parts.length ? parts.map((part) => `<span>${part}</span>`).join('') : '';
    },

    _openPoiLightbox(poiMarkerData) {
        const picture = this._firstPoiPicture(poiMarkerData);
        if (!picture) {
            return;
        }

        this._closePoiLightbox();

        const overlay = document.createElement('div');
        overlay.className = 'poi-lightbox';

        const image = document.createElement('img');
        image.className = 'poi-lightbox-img';
        image.src = (picture.urls && picture.urls.jpg) || picture.mediumUrls.jpg;
        image.alt = poiMarkerData.title || '';
        overlay.appendChild(image);

        const captionText = poiMarkerData.title || poiMarkerData.description || '';
        if (captionText) {
            const caption = document.createElement('div');
            caption.className = 'poi-lightbox-caption';
            caption.textContent = captionText;
            overlay.appendChild(caption);
        }

        // Attribution is mandatory for external (CC-licensed) media
        const credit = this._buildLightboxCredit(poiMarkerData.attribution);
        if (credit) {
            overlay.appendChild(credit);
        }

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'poi-lightbox-close';
        closeButton.setAttribute('aria-label', I18n.t('js.helpers.buttons.close'));
        closeButton.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/></svg>';
        overlay.appendChild(closeButton);

        // Backdrop / button click closes; clicks on the image or the credit do not
        overlay.addEventListener('click', (event) => {
            if (event.target === image || event.target.closest('.poi-lightbox-credit')) {
                return;
            }
            this._closePoiLightbox();
        });

        this._poiModel.poiLightboxKeyHandler = (event) => {
            if (event.key === 'Escape') {
                this._closePoiLightbox();
            }
        };
        document.addEventListener('keydown', this._poiModel.poiLightboxKeyHandler);

        document.body.appendChild(overlay);
        this._poiModel.poiLightbox = overlay;
    },

    _buildLightboxCredit(attribution) {
        if (!attribution || (!attribution.author && !attribution.license && !attribution.sourceUrl)) {
            return null;
        }

        const credit = document.createElement('div');
        credit.className = 'poi-lightbox-credit';

        const parts = [];
        if (attribution.author) {
            parts.push(`© ${attribution.author}`);
        }
        if (attribution.license) {
            parts.push(attribution.license);
        }

        if (parts.length > 0) {
            const info = document.createElement('span');
            info.className = 'poi-lightbox-credit-info';
            info.textContent = parts.join(' · ');
            credit.appendChild(info);
        }

        if (attribution.sourceUrl) {
            const link = document.createElement('a');
            link.className = 'poi-lightbox-credit-link';
            link.href = attribution.sourceUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = I18n.t('js.maps.poi.external_media.view_source');
            credit.appendChild(link);
        }

        return credit;
    },

    _closePoiLightbox() {
        if (this._poiModel.poiLightbox) {
            this._poiModel.poiLightbox.remove();
            this._poiModel.poiLightbox = null;
        }

        if (this._poiModel.poiLightboxKeyHandler) {
            document.removeEventListener('keydown', this._poiModel.poiLightboxKeyHandler);
            this._poiModel.poiLightboxKeyHandler = null;
        }
    },

    _addPoiMarkersPointEventHandlers(markerPoint, markerInfo, poiMarkerIndex, rideId) {
        markerPoint.marker.getElement()
            .addEventListener('click', function (poiPoint, poiInfo, event) {
                if (hasOpenMenu()) {
                    event.stopPropagation();
                    closeOpenMenus();
                    return;
                }

                event.stopPropagation();

                // On editable maps, clicking a POI opens its edit form and highlights it in the list
                if (this._model.poiEditable) {
                    this._poiHighlightedFuncs(poiInfo.data);
                    this._poiEditClickedFuncs(poiInfo.data, rideId);
                    return;
                }

                // With hover, the hover opens the popup; without, the tap does
                if (Utils.canHover()) {
                    return;
                }

                // Read this marker's own popup, not the map-wide hasPopup: without hover no leave
                // lowers it. Close the other POI popups, as the leave used to.
                if (poiPoint.marker.getPopup?.()?.isOpen?.()) {
                    return;
                }

                this._poiModel.poiMarkersPoints.forEach(({marker}) => {
                    if (marker !== poiPoint.marker && marker.getPopup?.()?.isOpen?.()) {
                        marker.togglePopup();
                    }
                });

                poiPoint.marker.togglePopup();
                this._model.hasPopup = true;
            }.bind(this, markerPoint, markerInfo));

        markerPoint.marker.getElement()
            .addEventListener('mouseenter', function (poiPoint, poiInfo) {
                // A tap's synthetic hover must change nothing, or iOS cancels the tap (edit form)
                if (!Utils.canHover()) {
                    return;
                }

                poiPoint.marker.togglePopup();
                this._model.hasPopup = true;
            }.bind(this, markerPoint, markerInfo));

        markerPoint.marker.getElement()
            .addEventListener('mouseleave', function (poiPoint, poiInfo) {
                if (!Utils.canHover()) {
                    return;
                }

                poiPoint.marker.togglePopup();
                this._model.hasPopup = false;
            }.bind(this, markerPoint, markerInfo));

        // Right-click / long-touch on an editable POI requests its deletion
        if (this._model.poiEditable) {
            const markerElement = markerPoint.marker.getElement();

            markerElement.addEventListener('contextmenu', (event) => {
                event.preventDefault();
                event.stopPropagation();

                this._poiDeleteRequestedFuncs(markerInfo.data, rideId);
            });

            let longTouchTimer = null;
            const cancelLongTouch = () => {
                if (longTouchTimer) {
                    clearTimeout(longTouchTimer);
                    longTouchTimer = null;
                }
            };

            markerElement.addEventListener('touchstart', () => {
                cancelLongTouch();
                longTouchTimer = setTimeout(() => {
                    this._poiDeleteRequestedFuncs(markerInfo.data, rideId);
                }, 600);
            }, {passive: true});

            markerElement.addEventListener('touchend', cancelLongTouch);
            markerElement.addEventListener('touchmove', cancelLongTouch);
        }

        // MapLibre Marker#on does NOT return the marker, so these handlers must be registered as
        // separate statements (not chained).
        // A waypoint can only slide ALONG the track: re-project it onto the curve while dragging.
        // Throttled to one projection per frame (projectOntoPolyline is O(points)).
        markerPoint.marker.on('drag', function (poiPoint, poiInfo) {
                if (!this._isWaypointPoi(poiInfo.data) || !this.projectOntoPolyline || this._poiModel.dragSnapRaf) {
                    return;
                }

                this._poiModel.dragSnapRaf = requestAnimationFrame(() => {
                    this._poiModel.dragSnapRaf = null;

                    const current = poiPoint.marker.getLngLat();
                    const projected = this.projectOntoPolyline({lat: current.lat, lng: current.lng});

                    if (projected) {
                        poiPoint.marker.setLngLat({lat: projected.lat, lng: projected.lng});
                    }
                });
            }.bind(this, markerPoint, markerInfo));

        markerPoint.marker.on('dragend', function (poiPoint, poiInfo) {
                if (this._poiModel.dragSnapRaf) {
                    cancelAnimationFrame(this._poiModel.dragSnapRaf);
                    this._poiModel.dragSnapRaf = null;
                }

                let newCoordinates = poiPoint.marker.getLngLat();
                const poi = poiInfo.data;

                // Snap the final position onto the curve (the throttled drag may have left the last
                // frame un-snapped), so a waypoint always persists on the track.
                if (this._isWaypointPoi(poi) && this.projectOntoPolyline) {
                    const projected = this.projectOntoPolyline({lat: newCoordinates.lat, lng: newCoordinates.lng});
                    if (projected) {
                        newCoordinates = {lat: projected.lat, lng: projected.lng};
                        poiPoint.marker.setLngLat(newCoordinates);
                    }
                }

                // Keep the cached coordinates in sync so a later redraw doesn't snap back
                poi.latitude = newCoordinates.lat;
                poi.longitude = newCoordinates.lng;

                // Let the parent handle the move when it registered a callback
                // (e.g. non-connected local POIs); otherwise persist through the API
                if (this._poiModel.poiMoved.length > 0) {
                    this._poiMovedFuncs(poi, {
                        lat: newCoordinates.lat,
                        lng: newCoordinates.lng
                    }, rideId);
                } else {
                    this.updateRidePoi(poi.id, {
                        latitude: newCoordinates.lat,
                        longitude: newCoordinates.lng
                    }, rideId);
                }
            }.bind(this, markerPoint, markerInfo));
    },

    _buildPoiMarkerPopup(rideId, poiMarkerIndex, poiMarkerInfo) {
        const poiMarkerData = poiMarkerInfo.data;

        const popupContent = document.createElement('div');
        popupContent.className = 'slideshow-popup';

        // Waypoint header: kind icon, then the user's title in bold (falling back to the default "Stop N"
        // when unnamed) with the place type as a lighter subtitle. Shown here so the popup always has
        // content and the title isn't repeated again below.
        if (this._isWaypointPoi(poiMarkerData)) {
            const header = document.createElement('div');
            header.className = 'poi-popup-waypoint-header';

            const icon = document.createElement('span');
            icon.className = isNightStop(poiMarkerData) ? 'poi-popup-waypoint-icon poi-popup-waypoint-icon--night' : 'poi-popup-waypoint-icon';
            icon.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${waypointIconPath(poiMarkerData)}"/></svg>`;
            header.appendChild(icon);

            const text = document.createElement('span');
            text.className = 'poi-popup-waypoint-text';

            const titleEl = document.createElement('span');
            titleEl.className = 'poi-popup-waypoint-title';
            titleEl.textContent = poiMarkerData.title
                || (poiMarkerData.number
                    ? I18n.t('js.maps.poi.waypoint_number', {number: poiMarkerData.number})
                    : I18n.t('js.maps.poi.waypoint'));
            text.appendChild(titleEl);

            // A titled plain step still says it is a step; an untitled one already reads "Étape".
            const kindLabel = this._waypointKindLabel(poiMarkerData) || (poiMarkerData.title ? I18n.t('js.ride.form.pois.waypoint_kind.step') : null);
            if (kindLabel) {
                const kindEl = document.createElement('span');
                kindEl.className = 'poi-popup-waypoint-kind';
                kindEl.textContent = kindLabel;
                text.appendChild(kindEl);
            }

            header.appendChild(text);
            popupContent.appendChild(header);
        }

        if (poiMarkerData.pictures.length > 0) {
            const slideShowContent = document.createElement('div');
            for (let pictureIndex = 0; pictureIndex < poiMarkerData.pictures.length; pictureIndex++) {
                const picture = poiMarkerData.pictures[pictureIndex];

                if (picture.mediumUrls && picture.mediumUrls.jpg) {
                    const imageContainer = document.createElement('div');
                    imageContainer.className = 'image';
                    if (pictureIndex === 0) {
                        imageContainer.className += ' active';
                    }

                    const imageContent = document.createElement('img');
                    imageContent.id = this._poiModel.poiPictureButtonId;
                    imageContent.src = picture.mediumUrls.jpg;
                    imageContent.dataset.rideId = rideId;
                    imageContent.dataset.poiMarkerIndex = poiMarkerIndex;
                    imageContent.setAttribute('data-ride-id', rideId);
                    imageContent.setAttribute('data-poi-id', poiMarkerData.id);
                    imageContent.setAttribute('data-poi-index', poiMarkerIndex);
                    imageContent.setAttribute('data-poi-picture-index', pictureIndex);

                    imageContainer.appendChild(imageContent);
                    slideShowContent.appendChild(imageContainer);
                }
            }

            const slideShow = document.createElement('div');
            slideShow.className = 'slideshow';
            slideShow.appendChild(slideShowContent);

            // Overlay the text on the bottom of the image (gradient for readability)
            if (poiMarkerData.title || poiMarkerData.description) {
                const caption = document.createElement('div');
                caption.className = 'poi-popup-caption';

                if (poiMarkerData.title) {
                    const captionTitle = document.createElement('span');
                    captionTitle.className = 'poi-popup-caption-title';
                    captionTitle.innerHTML = poiMarkerData.title;
                    caption.appendChild(captionTitle);
                }

                if (poiMarkerData.description) {
                    const captionDescription = document.createElement('span');
                    captionDescription.className = 'poi-popup-caption-description';
                    captionDescription.innerHTML = poiMarkerData.description;
                    caption.appendChild(captionDescription);
                }

                slideShow.appendChild(caption);
            }

            popupContent.appendChild(slideShow);

            if (poiMarkerData.pictures.length > 1) {
                const slideShowNavigation = document.createElement('div');
                slideShowNavigation.className = 'cycle';
                slideShowNavigation.innerHTML =
                    '<a href="#" class="prev">' + I18n.t('js.maps.slideshow.previous') + '</a>' +
                    '<a href="#" class="next">' + I18n.t('js.maps.slideshow.next') + '</a>';
                slideShowNavigation.onclick = function (currentSlideShow, event) {
                    event.preventDefault();

                    let $newSlide;

                    if (this.classList.contains('prev')) {
                        $newSlide = Utils.getPreviousElement(currentSlideShow.querySelector('.active'));
                        if ($newSlide.index() < 0) {
                            $newSlide = currentSlideShow.querySelectorAll('.image')
                                .last();
                        }
                    } else {
                        $newSlide = Utils.getNextElement(currentSlideShow.querySelector('.active'));
                        if ($newSlide.index() < 0) {
                            $newSlide = currentSlideShow.querySelectorAll('.image')
                                .first();
                        }
                    }

                    currentSlideShow.querySelector('.active')
                        .classList
                        .remove('active');
                    currentSlideShow.querySelector('.active').style.display = 'none';

                    $newSlide.classList.add('active');
                    $newSlide.style.display = 'block';

                    return false;
                }.bind(this, slideShow);
                popupContent.appendChild(slideShowNavigation);
            }
        }

        // Without an image, show the text as a plain block (with an image it is overlaid above).
        // Waypoints already show their title in the header above, so don't repeat it here.
        if (poiMarkerData.pictures.length === 0) {
            if (poiMarkerData.title && !this._isWaypointPoi(poiMarkerData)) {
                const title = document.createElement('p');
                title.className = 'poi-title';
                title.innerHTML = poiMarkerData.title;
                popupContent.appendChild(title);
            }

            if (poiMarkerData.description) {
                const description = document.createElement('div');
                description.className = 'poi-description';
                description.innerHTML = poiMarkerData.description;
                popupContent.appendChild(description);
            }
        }

        const metaHtml = this._buildPoiMeta(poiMarkerData);
        if (metaHtml) {
            const meta = document.createElement('div');
            meta.className = 'poi-popup-meta';
            meta.innerHTML = metaHtml;
            popupContent.appendChild(meta);
        }

        const popupContainer = document.createElement('div');
        popupContainer.className = poiMarkerInfo.type + '-popup'; // poi-popup
        popupContainer.appendChild(popupContent);

        return popupContainer.outerHTML;
    },

    _addPoiPopupMarkerEvents() {
        this._model.$mapId.addEventListener('click', (event) => {
            if (!event.target) {
                return;
            }

            if (event.target.matches('#' + this._poiModel.poiPictureButtonId)) {
                const rideId = parseInt(event.target.getAttribute('data-ride-id'));
                const poiMarkerIndex = parseInt(event.target.getAttribute('data-poi-marker-index'));
                // Use Number.isInteger so the first POI (index 0) opens fullscreen too
                if (Number.isInteger(rideId) && Number.isInteger(poiMarkerIndex)) {
                    this._handlePoiImageClick(rideId, poiMarkerIndex);
                }
            }
        });
    },

    _handlePoiImageClick(rideId, poiMarkerIndex) {
        this.leaveFullscreen();

        this._poiImageClickedFuncs(this._poiModel.poiFeatures[rideId].markerInfos[poiMarkerIndex].data, 0);
    },

    _poiImageClickedFuncs(poiData, pictureIndex) {
        for (let i = 0, n = this._poiModel.poiImageClicked.length; i < n; i++) {
            this._poiModel.poiImageClicked[i](poiData, pictureIndex);
        }
    },

    _poiAddClickedFuncs(coordinates) {
        for (let i = 0, n = this._poiModel.poiAddClicked.length; i < n; i++) {
            this._poiModel.poiAddClicked[i](coordinates);
        }
    },

    _poiEditClickedFuncs(poiData, rideId) {
        for (let i = 0, n = this._poiModel.poiEditClicked.length; i < n; i++) {
            this._poiModel.poiEditClicked[i](poiData, rideId);
        }
    },

    _poiHighlightedFuncs(poiData) {
        for (let i = 0, n = this._poiModel.poiHighlighted.length; i < n; i++) {
            this._poiModel.poiHighlighted[i](poiData);
        }
    },

    _poiMovedFuncs(poiData, coordinates, rideId) {
        for (let i = 0, n = this._poiModel.poiMoved.length; i < n; i++) {
            this._poiModel.poiMoved[i](poiData, coordinates, rideId);
        }
    },

    _poiDeleteRequestedFuncs(poiData, rideId) {
        for (let i = 0, n = this._poiModel.poiDeleteRequested.length; i < n; i++) {
            this._poiModel.poiDeleteRequested[i](poiData, rideId);
        }
    },

    _poiLoadedFuncs(rideId, poisData) {
        for (let i = 0, n = this._poiModel.poiLoadedCallback.length; i < n; i++) {
            this._poiModel.poiLoadedCallback[i](rideId, poisData);
        }
    },

    // Undo
    // _pushUndoSnapShot() {
    //     const markers = [];
    //     for (i = 0; i < this._mapModel.markersInfo.length; i++) {
    //         const markersInfo = this._mapModel.markersInfo[i];
    //         markers.push(new MapInterface.MarkerInfo(markersInfo.latLng, markersInfo.type, markersInfo.data));
    //     }
    //     this._mapModel.markerStack.push(markers);
    // },

    // _revertToSnapshot(snapShotIndex) {
    //     if (typeof snapShotIndex === 'undefined' || snapShotIndex === null) {
    //         snapShotIndex = this._mapModel.markerStack.length - 1;
    //     }
    //
    //     if (snapShotIndex < 0) {
    //         return;
    //     }
    //
    //     const newMarkersInfo = this._mapModel.markerStack[snapShotIndex];
    //
    //     const diffMarkersInfo = this._mapModel.markersInfo.filter((markerInfo) => {
    //         return !newMarkersInfo.some((newMarkerInfo) => newMarkerInfo['latLng'] === markerInfo['latLng']);
    //     });
    //
    //     this._mapModel.markersInfo = newMarkersInfo;
    //
    //     numberToPop = this._mapModel.markerStack.length - snapShotIndex;
    //     for (i = 0; i < numberToPop; i++) {
    //         this._mapModel.markerStack.pop();
    //     }
    //
    //     diffMarkersInfo.forEach((markerInfo) => {
    //         if (markerInfo.type === 'poi') {
    //             this._mapModel.poiPointMarkers = this._mapModel.poiPointMarkers.filter((poiPointMarker) => poiPointMarker.data.id !== markerInfo.data.id);
    //
    //             this._poiChangedFuncs('delete', markerInfo);
    //         }
    //     });
    //
    //     this._drawMarkersPoints();
    //     this._updateMarkerPoints();
    //     this._markerChangedFuncs();
    // },

    _removePoiMarkersPoints(rideId) {
        if (!!rideId && !!this._poiModel.poiFeatures[rideId]) {
            this.resetMapSource('poi-marker');
        }
    }
};

export default RidePoisMapModule;
